import { APP_CLIENTS } from '@RootCommon/constants'
import { CSIM_MESSAGE_SOURCE } from './constant'

/**
 * URL载荷参数类型
 */
export interface UrlPayload {
  /** appkey，客服配置平台的appkey */
  from: CSIMAppKeyEnum
  /** B端系统菜单位置, 默认是 HOME */
  sideBarPosition?: CSIMSceneEnum
  /** 客服系统页面路由 */
  targetRoute: PageRouteEnum
  /** 页面版本标识，用于区分新版和老版页面，巴西地区要使用GCC版本，其他地区使用老版 */
  csimVersion?: 'GCC' | ''
  /** 端类型 */
  clientType: ClientTypeEnum
  /** 环境 */
  env?: string
  /** 语言 */
  locale?: string
  /** 地区，公参，要和上下游保持一致 */
  region?: string
  /** 城市ID */
  cityId?: string | number
  /** 店铺信息 */
  shopInfo?: {
    /** 店铺ID */
    shopId: string
    /** 店铺名称 */
    name: string
  }
  /** 是否在App中 */
  isInApp?: boolean
  /** 自定义扩展参数 */
  [key: string]: any
  /** 订单ID */
  orderId?: string
  /** 语言 */
  language?: string
  /** 自定义扩展参数, 会被 encodeURIComponent 两次 */
  ext?: Record<string, any>
}

/**
 * 默认错误提示文本
 */
export const DEFAULT_ERROR_MESSAGES = {
  loadFailed: 'Loading Failed',
  retry: 'Retry',
} as const

/**
 * 错误边界组件Props类型
 */
export interface CSIMErrorBoundaryProps {
  /** 子组件 */
  children: React.ReactNode
  /** 错误时的备用组件 */
  fallback?: React.ReactNode
}

/**
 * 错误边界组件State类型
 */
export interface CSIMErrorBoundaryState {
  /** 是否有错误 */
  hasError: boolean
}

export type HeaderActionType = 'open' | 'collapse' | 'close' | 'fullscreen' | 'newWindow'

/**
 * 位置边距配置类型
 */
export interface PositionMargin {
  /** 顶部边距 */
  top?: number
  /** 底部边距 */
  bottom?: number
  /** 左侧边距 */
  left?: number
  /** 右侧边距 */
  right?: number
  /** 水平边距（同时设置left和right） */
  x?: number
  /** 垂直边距（同时设置top和bottom） */
  y?: number
}

/**
 * CSIM组件Props类型
 */
export interface CSIMProps {
  /** 触发按钮内容 */
  children?: React.ReactNode
  /** 目标URL参数 */
  urlPayload: UrlPayload
  /** 父节点 */
  // 当指定 parentNode 时，需确保容器需设置 position: relative，否则可能导致定位偏差。
  parentNode?: HTMLElement
  /** 自定义样式 */
  style?: React.CSSProperties
  /** 全屏时的自定义样式 */
  fullScreenStyle?: React.CSSProperties
  /** 是否打开浮层 */
  visible?: boolean
  /** 位置边距配置，用于覆盖默认的DEFAULT_POSITION_MARGIN */
  positionMargin?: PositionMargin
  /** csim children click handle */
  onTriggerClick?: () => void
  /** csim 头部操作按钮的 */
  onHeaderActionsClick?: (actionType: HeaderActionType) => void
  /** 打开回调 */
  onOpen?: () => void
  /** 关闭回调 */
  onClose?: () => void
  /** 收起回调 */
  onCollapse?: () => void
  /** 新窗口打开回调 */
  onOpenNewWindow?: (url: string) => void
  /** 全屏回调 */
  onFullscreen?: (isFullscreen: boolean) => void
  /** 跳转回调 */
  onNavigate?: (url: string, payload?: any) => void
  /** 加载失败提示文本 */
  errorMessages?: {
    /** 加载失败提示 */
    loadFailed: string
    /** 重试按钮文本 */
    retry: string
  }
  /** 是否显示收起按钮 */
  showCollapse?: boolean
  /** 是否显示关闭按钮 */
  showClose?: boolean
  /** 是否显示新窗口打开按钮 */
  showNewWindow?: boolean
  /** 是否显示全屏按钮 */
  showFullscreen?: boolean
}

/**
 * B端菜单枚举
 */
export enum CSIMSceneEnum {
  /** 服务门户首页 */
  HOME = 1,
  /** 订单FAQ列表 */
  ORDER_FAQ = 2,
  /** 排队中页面 */
  QUEUE_PAGE = 3,
  /** 商家管理后台首页 */
  MERCHANT_OVERVIEW = 4,
  /** 商家web运营分析 */
  MERCHANT_OPERATION_ANALYSIS = 5,
  /** 商家健康度 */
  MERCHANT_HEALTH_RATING = 6,
  /** 商家web订单管理 */
  MERCHANT_ORDER = 7,
  /** 商家web财务管理 */
  MERCHANT_FINANCIAL = 8,
  /** 商家web顾客管理 */
  MERCHANT_CUSTOMER_REVIEWS = 9,
  /** 商家菜单管理 */
  MERCHANT_PRODUCT = 10,
  /** 商家广告管理 */
  MERCHANT_ADVERTISING = 11,
  /** 商家web营销中心 */
  MERCHANT_PROMOTION_CENTER = 12,
  /** 商家web设置 */
  MERCHANT_OPERATION_SETTING = 13,
}

/**
 * 应用Key枚举
 * 基于客服系统配置表生成
 */
export enum CSIMAppKeyEnum {
  // ==================== C端（用户端） ====================
  /** C端-个人中心Customer service固定全场景入口 */
  WAIMAI_WAIMAI_HOME = 'waimai_waimai_home',
  /** C端-登录页面场景化入口 */
  WAIMAI_WAIMAI_LOGIN_QUESTION = 'waimai_waimai_login_question',
  /** C端-订单详情页右上角场景化入口 */
  WAIMAI_WAIMAI_ORDER = 'waimai_waimai_order',
  /** C端-准时宝页面场景化入口 */
  WAIMAI_WAIMAI_QUESTION = 'waimai_waimai_question',
  /** C端-隐私政策入口场景化入口 */
  WAIMAI_WAIMAI_CHAT_PRIVACY_COMPLIANCE = 'waimai_waimai_chat_privacy_compliance',
  /** C端-商家IM场景化入口（新增） */
  WAIMAI_WAIMAI_MERCHANT_IM = 'waimai_waimai_merchant_im',
  /** C端-退款详情页固定入口场景化入口 */
  WAIMAI_WAIMAI_REFUND_DETAIL = 'waimai_waimai_refund_detail',
  /** C端-信客失败客服入口场景化入口 */
  WAIMAI_WAIMAI_USER_REFUND_DEFAULT = 'waimai_waimai_user_refund_default',
  /** C端-退款页面直接转人工入口 */
  WAIMAI_WAIMAI_REFUND_CHAT = 'waimai_waimai_refund_chat',
  /** C端-券包订单页面直接转人工入口 */
  WAIMAI_WAIMAI_COUPON_PACK_REFUND_CHAT = 'waimai_waimai_coupon_pack_refund_chat',
  /** C端-作废迟滞工单信息 */
  WAIMAI_WAIMAI_EXTERNAL_CASE_CANCEL = 'waimai_waimai_externalCaseCancel',
  /** C端-创建迟滞工单信息 */
  WAIMAI_WAIMAI_EXTERNAL_CASE_CREATE = 'waimai_waimai_externalCaseCreate',
  /** C端-用户申请退款-系统无法判责创建迟滞工单 */
  WAIMAI_USER_APPLY_REFUND = 'waimai_user_apply_refund',
  /** C端-长时间订单未完成，需要赔付用户券 */
  WAIMAI_USER_ORDER_OVERTIME = 'waimai_user_order_overtime',
  /** C端-无骑手接单干预迟滞工单 */
  WAIMAI_USER_ORDER_OVERTIME_NO_RIDER = 'waimai_user_order_overtime_no_rider',
  /** C端-用户拉黑骑手聊天页面 */
  WAIMAI_USER_SHIELD_RIDER = 'waimai_user_shield_rider',
  /** C端-用户端app个人页 */
  WAIMAI_USER_HOME_APP = 'waimai_user_home_app',
  /** C端-退款原因选择页进入迟滞工单 */
  WAIMAI_USER_REFUND_CASE = 'waimai_user_refund_case',
  /** C端-im消息push */
  WAIMAI_USER_IM_PUSH = 'waimai_user_im_push',
  /** C端-封斋期间长时间订单未完成创建的OSLO工单 */
  WAIMAI_RAMADAN_USER_ORDER_OVERTIME = 'waimai_ramadan_user_order_overtime',

  // ==================== B端（商家端） ====================
  /** B端-PC管理后台toolbar固定全场景入口 */
  WAIMAI_SHOP_TOOLBAR = 'waimai_shop_toolbar',
  /** B端-PC自入驻官网场景化入口 */
  WAIMAI_SHOP_REGISTER = 'waimai_shop_register',
  /** B端-PC管理后台登录页面场景化入口 */
  WAIMAI_SHOP_PC_LOGIN_QUESTION = 'waimai_shop_pc_login_question',
  /** B端-PC管理后台订单详情页场景化入口 */
  WAIMAI_SHOP_ORDER = 'waimai_shop_order',
  /** B端-商家端个人中心固定全场景入口 */
  WAIMAI_SHOP_HOME = 'waimai_shop_home',
  /** B端-商家端悬浮窗 */
  WAIMAI_SHOP_FLOATING_WINDOW = 'waimai_shop_floating_window',
  /** B端-商家端悬浮窗-app */
  WAIMAI_SHOP_FLOATING_WINDOW_APP = 'waimai_shop_floating_window_app',
  /** B端-商家端app匿名用户登录页场景化入口 */
  WAIMAI_SHOP_APP_LOGIN_QUESTION = 'waimai_shop_app_login_question',
  /** B端-商家端app帮助中心固定全场景入口 */
  WAIMAI_SHOP_APP_HOME = 'waimai_shop_app_home',
  /** B端-商家端pos匿名用户登录页 */
  WAIMAI_SHOP_POS_LOGIN_QUESTION = 'waimai_shop_pos_login_question',
  /** B端-商家端自入驻顶栏，无线索阶段 */
  WAIMAI_SHOP_OPERATION_SETTING = 'waimai_shop_operation_setting',
  /** B端-商家端订单申诉-发起工单 */
  WAIMAI_SHOP_CREATE_APPEAL = 'waimai_shop_create_appeal',
  /** B端-商家端订单申诉-取消工单 */
  WAIMAI_SHOP_CANCEL_APPEAL = 'waimai_shop_cancel_appeal',
  /** B端-商家端im消息push */
  WAIMAI_SHOP_IM_PUSH = 'waimai_shop_im_push',
  /** B端-商家自入驻过程中入口 */
  WAIMAI_SHOP_OFFICIAL_WEB_ANONYMOUS_QUESTION = 'waimai_shop_official_web_anonymous_question',

  // ==================== B端（商家端）-PC接单系统(windows端) ====================
  /** B端-PC接单系统固定入口固定全场景入口 */
  WAIMAI_ORDER_MANAGER_TOOLBAR_WINDOWS = 'waimai_order_manager_toolbar',
  /** B端-PC接单系统登录页面场景化入口 */
  WAIMAI_ORDER_MANAGER_LOGIN_WINDOWS = 'waimai_order_manager_login',
  /** B端-PC接单系统订单详情页场景化入口 */
  WAIMAI_ORDER_MANAGER_ORDER_WINDOWS = 'waimai_order_manager_order',

  // ==================== D端（骑手端） ====================
  /** D端-侧边栏固定入口固定全场景入口 */
  WAIMAI_RIDER_HOME = 'waimai_rider_home',
  /** D端-异常上报-取餐前故障页场景化入口 */
  WAIMAI_RIDER_ORDER = 'waimai_rider_order',
  /** D端-异常上报-用户修改地址故障页场景化入口 */
  WAIMAI_RIDER_CHAT = 'waimai_rider_chat',
  /** D端-匿名用户登录页 */
  WAIMAI_RIDER_LOGIN_QUESTION = 'waimai_rider_login_question',
  /** D端-异常上报-取餐后故障页场景化入口 */
  WAIMAI_RIDER_AFTER_ORDER = 'waimai_rider_after_order',
  /** D端-异常上报-联系客服转单场景化入口 */
  WAIMAI_RIDER_REASSIGN_ORDER = 'waimai_rider_reassign_order',
  /** D端-im消息push */
  WAIMAI_RIDER_IM_PUSH = 'waimai_rider_im_push',

  // ==================== Keemart相关 ====================
  /** Keemart-订单详情页面 */
  KEEMART_USER_ORDER = 'keemart_user_order',
  /** Keemart-售后申请页面 */
  KEEMART_USER_AFTER_SALE_ORDER = 'keemart_user_after_sale_order',
  /** Keemart-用户申请退款创建迟滞工单 */
  KEEMART_USER_APPLY_REFUND = 'keemart_user_apply_refund',
  /** Keemart-判责创建申诉工单创建迟滞工单 */
  KEEMART_USER_BLAME_APPEAL = 'keemart_user_blame_appeal',
  /** Keemart-骑手服务门户首页 */
  KEEMART_RIDER_HOME = 'keemart_rider_home',
  /** Keemart-用户端FAQ页超链进入客服 */
  KEEMART_USER_FAQ_CHAT = 'keemart_user_faq_chat',
  /** Keemart-骑手端FAQ页超链进入客服 */
  KEEMART_RIDER_FAQ_CHAT = 'keemart_rider_faq_chat',
}

/**
 * 页面路由枚举
 * 基于客服系统路由生成
 * ! 注意，巴西地区得使用巴西首页
 */
export enum PageRouteEnum {
  /** 首页 */
  HOME = '/home',
  /** 巴西首页 */
  HOME_BR = '/homebr',
  /** 订单页面 */
  ORDER = '/order',
  /** 问题页面 */
  QUESTION = '/question',
  /** 答案页面 */
  ANSWER = '/answer',
  /** 聊天页面 */
  CHAT = '/chat',
  /** 服务进度页面 */
  PROGRESS = '/progress',
  /** 订单表单页面 */
  ORDER_FORM = '/orderform',
  /** 提交成功页面 */
  SUBMIT_SUCCESS = '/submitSuccess',
  /** 历史消息页面 */
  HISTORY_MESSAGE = '/historyMessage',
  /** 评价页面 */
  EVALUATE = '/evaluate',
  /** 地图页面 */
  MTMAP = '/mtmap',
  /** 上传凭证页面 */
  UPLOAD_CREDENTIALS = '/uploadCredentials',
}

/**
 * 客户端类型
 * 直接使用 APP_CLIENTS 的值类型
 */
export type ClientTypeEnum = typeof APP_CLIENTS[keyof typeof APP_CLIENTS]

/**
 * 消息处理器配置选项
 */
export type HostMessageHandlerOptions = {
  /** iframe选择器 */
  iframeId: string
  /** 打开URL的处理函数 */
  openUrlHandle?: (url: string, payload?: any) => void
}


/**
 * 全屏事件详情类型
 */
export interface CSIMIframeContentEventDetail {
  timestamp: number;
  source: typeof CSIM_MESSAGE_SOURCE;
  iframeId?: string; // 新增：iframe唯一标识
}